import {
  Component,
  OnInit,
  Input,
  SimpleChanges,
  OnChanges,
  Output,
  EventEmitter,
} from '@angular/core';
import { ReactiveFormsModule, FormControl, FormsModule } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatSelectModule } from '@angular/material/select';
import { MatChipsModule } from '@angular/material/chips';
import { NgToastService } from 'ng-angular-popup';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { Validators } from '@angular/forms';
import { CommonModule } from '@angular/common';
import { EoiSectionsComponent } from '../eoi-sections.component';
import { MatIcon } from '@angular/material/icon';

import { OrganizationService } from '@/app/services/organization/organization.service';
import { TenderingService } from '@/app/services/tendering-services/tendering.service';

@Component({
  selector: 'app-companies-selection',
  standalone: true,
  imports: [
    MatChipsModule,
    MatButtonModule,
    MatSelectModule,
    ReactiveFormsModule,
    FormsModule,
    CommonModule,
    ReactiveFormsModule,
    MatFormFieldModule,
    MatInputModule,
    MatIcon,
  ],
  templateUrl: './companies-selection.component.html',
  styleUrl: './companies-selection.component.css',
})
export class CompaniesSelectionComponent implements OnInit, OnChanges {
  @Input() eoiId!: number;
  @Input() projectId!: number;
  @Input() currentEOISavedData: any = [];
  @Output() responseEmitter = new EventEmitter<any>();
  projectName: string;
  allOrganizations: any;
  filteredOrganizations: any;
  receiverOrganizationsList: any[] = [];
  selectedReceiverOrganization: any;
  searchTerm = '';
  isExternalOrg = false;
  externalOrgNameControl = new FormControl(null);
  nameControl = new FormControl(null, Validators.required);
  positionControl = new FormControl(null, Validators.required);
  emailControl = new FormControl(null, [Validators.required, Validators.email]);
  selectedReceiverOrganizationControl = new FormControl(
    null,
    Validators.required
  );

  constructor(
    private toast: NgToastService,
    private OrganizationService: OrganizationService,
    private EoiSectionsComponent: EoiSectionsComponent,
    private tenderingService: TenderingService
  ) {}

  ngOnInit(): void {
    this.projectName = localStorage.getItem('project-name');
    this.fetchAllOrganization();

    this.isEOISavedDataExist();
  }
  ngOnChanges(changes: SimpleChanges): void {
    if (changes['currentEOISavedData']) {
      this.isEOISavedDataExist();
    }
  }

  isEOISavedDataExist() {
    this.receiverOrganizationsList = [];
    if (this.currentEOISavedData?.eoiData?.selected_organizations?.length) {
      this.currentEOISavedData.eoiData.selected_organizations.forEach(
        (organization: any) => {
          this.receiverOrganizationsList.push({
            company_name: organization?.receiving_org_id
              ? organization?.applicant_organization?.name
              : organization?.external_company_name,
            name: organization?.name,
            email: organization?.email,
            position: organization?.position,
            id: organization?.receiving_org_id || null,
            type: organization?.receiving_org_id ? 'system' : 'external',
          });
        }
      );
      if (this.receiverOrganizationsList?.length > 0) {
        this.filterReceiverOrganizationList();
      }
    }
  }

  externalOrgOptionSelection() {
    this.isExternalOrg = true;
    this.externalOrgNameControl.setValue('');
    this.nameControl.setValue('');
    this.emailControl.setValue('');
    this.positionControl.setValue('');

    // Make other company field required
    this.updateExternalCompanyValidation(true);
  }

  removeOrFetchExternalCompanyDetails(event: KeyboardEvent) {
    if (event.key === 'Enter') {
      this.fetchReceiverOrganizationEOIDetails('');
      return;
    }
    this.updateOrganizationValues([]);
  }

  updateExternalCompanyValidation(flag = false) {
    if (flag) {
      this.externalOrgNameControl.setValidators([Validators.required]);
    } else {
      this.externalOrgNameControl.clearValidators();
    }
    this.externalOrgNameControl.updateValueAndValidity();
  }

  /**
   * When user clicks `Add` to insert a new organization to Receiver organizations list,
   */
  addReceiverOrganizationDetails() {
    /**
     * Validation if other company is selected required fields are filled or not
     * Mark all fields as touched to trigger validation
     */
    this.nameControl.markAsTouched();
    this.emailControl.markAsTouched();
    this.positionControl.markAsTouched();
    this.selectedReceiverOrganizationControl.markAsTouched();

    if (this.isExternalOrg) {
      this.externalOrgNameControl.markAsTouched();
    }

    // Check if any field is invalid
    if (
      this.nameControl.invalid ||
      this.emailControl.invalid ||
      this.positionControl.invalid ||
      (this.isExternalOrg && this.externalOrgNameControl.invalid) ||
      (!this.isExternalOrg && !this.selectedReceiverOrganizationControl.value)
    ) {
      this.toast.warning('Please fill the required fields', 'ERROR');
      return;
    }

    // If validation passes, create the object
    this.receiverOrganizationsList.push({
      company_name: this.selectedReceiverOrganization?.name
        ? this.selectedReceiverOrganization?.name
        : this.externalOrgNameControl.value || null,
      name: this.nameControl.value,
      email: this.emailControl.value,
      position: this.positionControl.value,
      id: this.selectedReceiverOrganization?.id || null,
      type: this.selectedReceiverOrganization?.id ? 'system' : 'external',
    });

    this.toast.success('Organization added', 'success');
    // Clear fields and reset the form controls
    this.selectedReceiverOrganizationControl.reset();
    this.externalOrgNameControl.reset();
    this.nameControl.reset();
    this.emailControl.reset();
    this.positionControl.reset();
    this.selectedReceiverOrganization = null; // Reset the selected organization
    this.isExternalOrg = false; // Reset 'other' selection

    // NOTE: Filter the current list of organization and remove this organization
    this.filterReceiverOrganizationList();

    // NOTE: remove validations from the other_company field
    this.updateExternalCompanyValidation(false);
  }

  /**
   * NOTE: removed all organizations that are in receiverOrganizationsList from filteredOrganizations
   */
  filterReceiverOrganizationList() {
    if (!Array.isArray(this.filteredOrganizations)) {
      this.filteredOrganizations = []; // Initialize if not already
    }

    const organizations = this.filteredOrganizations.filter(
      (organization) =>
        !this.receiverOrganizationsList.some(
          (receiverOrganization) =>
            receiverOrganization?.id === organization?.id
        )
    );
    this.filteredOrganizations = organizations;
  }

  /**
   * When user selects/clicks on an organization from ddl
   */
  setCurrentReceiverOrganization() {
    this.selectedReceiverOrganization =
      this.selectedReceiverOrganizationControl.value;
  }

  updateOrganizationValues(data) {
    this.nameControl.setValue(data?.name || '');
    this.emailControl.setValue(data?.email || '');
    this.positionControl.setValue(data?.position || '');
  }

  removeOrganization(org: string) {
    const index = this.receiverOrganizationsList.indexOf(org);
    if (index >= 0) {
      this.receiverOrganizationsList.splice(index, 1);
    }
  }

  /**
   * When user starts writing in DDL, filter results
   */
  filterOrganizationsInDDL() {
    this.filteredOrganizations = this.allOrganizations.filter((org: any) =>
      org.name.toLowerCase().includes(this.searchTerm.toLowerCase())
    );
  }

  moveToPreviousStep() {
    // this.EoiSectionsComponent.previousStep();
  }

  handleBack() {
    this.receiverOrganizationsList = [];
    this.EoiSectionsComponent.previousStep();
  }

  /**
   * HTTP REQUESTS START
   */

  fetchAllOrganization() {
    this.OrganizationService.getAllOrganization().subscribe({
      next: (response: any) => {
        this.allOrganizations = response.data || []; // Default to an empty array if no data
        this.filteredOrganizations = [...this.allOrganizations]; // Initialize with a copy
      },
      error: (error: any) => {
        this.toast.danger(error);
        this.allOrganizations = []; // Prevent null or undefined access
        this.filteredOrganizations = [];
      },
    });
  }

  /**
   * when user selects organization, get its saved details
   */
  fetchReceiverOrganizationEOIDetails(org: any) {
    if (org?.id) {
      this.isExternalOrg = false;
      this.externalOrgNameControl.setValue('');
    }

    const dataToSubmit = {
      id: org?.id || '',
      name: this.externalOrgNameControl.value || '',
    };

    // If external organization and is empty i.e. user clicked outsite dont search
    if (!org?.id && !this.externalOrgNameControl.value) {
      return;
    }

    this.tenderingService
      .getEOIReceiverOrganizationDetails(dataToSubmit)
      .subscribe({
        next: (response: any) => {
          if (response?.data) {
            this.updateOrganizationValues(response.data);
          }
        },
        error: (error: any) => {
          console.error('API Error:', error);
          this.updateOrganizationValues([]);
        },
      });
  }

  moveToStep3() {
   

    const dataToSubmit = { receiving_org_data: this.receiverOrganizationsList };

    if (this.receiverOrganizationsList.length === 0) {
      this.nameControl.markAsTouched();
      this.emailControl.markAsTouched();
      this.positionControl.markAsTouched();
      this.selectedReceiverOrganizationControl.markAsTouched();
      this.selectedReceiverOrganizationControl.setErrors({ required: true });
      this.toast.warning('Please add at least one organization.', 'Validation Error');
      return; // Stop execution if no organization is added
    }else {
      // Clear the validation error if organizations are present
      this.selectedReceiverOrganizationControl.setErrors(null);
  }

    this.tenderingService
      .storeEOIStep2Data(dataToSubmit, this.projectId, this.eoiId)
      .subscribe({
        next: (response: any) => {
          if (response?.meta?.success) {
            this.toast.success('Companies selection completed', 'SUCCESS');
            this.tenderingService.setEoiId(this.eoiId);
            this.EoiSectionsComponent.nextStep();
            this.receiverOrganizationsList = [];
            this.responseEmitter.emit({
              eoiId: this.eoiId,
              projectId: this.projectId,
              updatedOrganization: response?.data,
            });
          } else {
            this.toast.danger(
              'Error in saving Expression of Interest company details',
              'DANGER',
              4000
            );
          }
        },
        error: (error: any) => {
          console.error('Failed to submit:', error);
          this.toast.danger(error.error.message, 'DANGER', 4000);
        },
      });
  }

  resetFormControls() {
    this.selectedReceiverOrganizationControl.reset();
    this.externalOrgNameControl.reset();
    this.nameControl.reset();
    this.emailControl.reset();
    this.positionControl.reset();
    this.selectedReceiverOrganization = null; // Reset the selected organization
    this.isExternalOrg = false; // Reset 'other' selection
  }
}